package com.schoolconnect.app.repository.parent

import com.schoolconnect.app.datasource.network.KtorApiClient
import com.schoolconnect.app.datasource.network.response.StudentListResponse
import com.schoolconnect.app.datasource.network.response.ParentListResponse
import com.schoolconnect.app.datasource.network.response.StudentParentListResponse
import com.schoolconnect.app.datasource.network.response.serverTotalCountOrNull
import com.schoolconnect.app.datasource.network.response.withServerTotalCount
import com.schoolconnect.app.datasource.network.request.StudentParentWrapperCreateRequest
import com.schoolconnect.app.datasource.network.request.ParentUpdateRequest
import com.schoolconnect.app.datasource.network.request.StudentParentLinkUpdateRequest
import com.schoolconnect.app.datasource.network.request.ParentCreateRequest
import com.schoolconnect.app.datasource.network.request.StudentParentLinkCreateRequest
import com.schoolconnect.app.datasource.network.request.ParentFilterRequest
import kotlinx.serialization.json.JsonObject
import com.schoolconnect.app.repository.common.loadPagedItems
import com.schoolconnect.app.repository.common.returnCachedAndRefresh
import com.schoolconnect.app.feature.common.PaginationPolicy
import com.schoolconnect.app.feature.common.ServerSort
import com.schoolconnect.app.util.scLog
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.SupervisorJob

object ParentRepository {
    private const val TAG = "[DATA_RETRIEVE_SC] ParentRepository"

    private data class ParentsPageCacheKey(
        val schoolId: String,
        val query: String,
        val classSectionId: String?,
        val isActive: Boolean?,
        val pageIndex: Int,
        val pageSize: Int,
        val sort: String
    )

    private data class CachedParents(
        val schoolId: String,
        val parents: List<StudentListResponse>
    )

    private data class CachedParentsPage(
        val key: ParentsPageCacheKey,
        val parents: List<StudentListResponse>
    )

    private val scope = CoroutineScope(SupervisorJob() + Dispatchers.Default)
    private var cachedSchoolParents: CachedParents? = null
    private var cachedParentsPage: CachedParentsPage? = null

    suspend fun createParentAndLink(
        request: StudentParentWrapperCreateRequest,
        schoolId: String,
        currentUserId: String? = null
    ): Result<JsonObject> {
        val studentId = request.studentId.trim()
        val normalizedSchoolId = schoolId.trim()
        require(studentId.isNotBlank()) { "studentId is required" }
        require(normalizedSchoolId.isNotBlank()) { "schoolId is required" }
        scLog("$TAG createParentAndLink start studentId=$studentId schoolId=$normalizedSchoolId")
        return KtorApiClient.createParentsAndLink(
            request = request.copy(studentId = studentId),
            currentUserId = currentUserId
        ).onSuccess {
            invalidateCache(normalizedSchoolId)
            scLog("$TAG createParentAndLink complete studentId=$studentId schoolId=$normalizedSchoolId")
        }.onFailure { throwable ->
            scLog("$TAG createParentAndLink failure studentId=$studentId schoolId=$normalizedSchoolId", throwable)
        }
    }

    suspend fun updateParent(
        parentId: String,
        request: ParentUpdateRequest,
        schoolId: String,
        currentUserId: String? = null
    ): Result<Unit> {
        val normalizedParentId = parentId.trim()
        val normalizedSchoolId = schoolId.trim()
        require(normalizedParentId.isNotBlank()) { "parentId is required" }
        require(normalizedSchoolId.isNotBlank()) { "schoolId is required" }
        scLog("$TAG updateParent start parentId=$normalizedParentId schoolId=$normalizedSchoolId")
        return KtorApiClient.updateParent(
            parentId = normalizedParentId,
            request = request.copy(schoolId = normalizedSchoolId),
            currentUserId = currentUserId
        ).map { invalidateCache(normalizedSchoolId) }
            .onFailure { throwable -> scLog("$TAG updateParent failure parentId=$normalizedParentId", throwable) }
    }

    suspend fun updateParentLink(
        linkId: String,
        request: StudentParentLinkUpdateRequest
    ): Result<Unit> {
        val normalizedLinkId = linkId.trim()
        require(normalizedLinkId.isNotBlank()) { "linkId is required" }
        return KtorApiClient.updateStudentParentLink(normalizedLinkId, request)
            .map { invalidateCache() }
            .onFailure { throwable -> scLog("$TAG updateParentLink failure linkId=$normalizedLinkId", throwable) }
    }

    suspend fun setParentLinkActive(linkId: String, isActive: Boolean): Result<Unit> {
        val normalizedLinkId = linkId.trim()
        require(normalizedLinkId.isNotBlank()) { "linkId is required" }
        scLog("$TAG setParentLinkActive start linkId=$normalizedLinkId isActive=$isActive")
        return KtorApiClient.toggleStudentParentLinkStatus(normalizedLinkId, isActive)
            .map { invalidateCache() }
            .onFailure { scLog("$TAG setParentLinkActive failure linkId=$normalizedLinkId", it) }
    }

    suspend fun deleteParentLink(linkId: String): Result<Unit> {
        val normalizedLinkId = linkId.trim()
        require(normalizedLinkId.isNotBlank()) { "linkId is required" }
        scLog("$TAG deleteParentLink start linkId=$normalizedLinkId")
        return KtorApiClient.deleteStudentParentLink(normalizedLinkId)
            .map { invalidateCache() }
            .onFailure { scLog("$TAG deleteParentLink failure linkId=$normalizedLinkId", it) }
    }

    suspend fun createAdditionalParentAndLink(
        schoolId: String,
        studentId: String,
        name: String,
        mobile: String,
        email: String?,
        relationshipType: String,
        isActive: Boolean,
        currentUserId: String?
    ): Result<Unit> {
        val normalizedSchoolId = schoolId.trim()
        val normalizedStudentId = studentId.trim()
        val parent = KtorApiClient.createParent(
            ParentCreateRequest(
                schoolId = normalizedSchoolId,
                name = name.trim(),
                mobile = mobile.trim(),
                email = email?.trim()?.ifBlank { null },
                isActive = isActive
            ),
            currentUserId
        ).getOrElse { return Result.failure(it) }
        val parentId = parent.id?.trim().orEmpty()
        if (parentId.isBlank()) {
            return Result.failure(IllegalStateException("Parent create response did not include an id."))
        }
        return KtorApiClient.createStudentParentLink(
            StudentParentLinkCreateRequest(
                studentId = normalizedStudentId,
                parentId = parentId,
                relationshipType = relationshipType,
                isPrimaryContact = false,
                isActive = isActive
            )
        ).map { invalidateCache(normalizedSchoolId) }
    }

    suspend fun loadStudentsWithParentsPage(
        schoolId: String,
        query: String = "",
        isActive: Boolean? = true,
        pageIndex: Int = 0,
        pageSize: Int = PaginationPolicy.DEFAULT_PAGE_SIZE,
        sort: ServerSort = ServerSort("admission_number", com.schoolconnect.app.feature.common.ServerSortOrder.ASC),
        forceRemote: Boolean = false
    ): Result<List<StudentListResponse>> {
        val normalizedSchoolId = schoolId.trim()
        if (normalizedSchoolId.isBlank()) {
            return Result.failure(IllegalArgumentException("schoolId is required"))
        }
        val safePageIndex = pageIndex.coerceAtLeast(0)
        val safePageSize = pageSize.coerceIn(1, 100)
        val normalizedQuery = query.trim()
        val offset = safePageIndex * safePageSize
        scLog(
            "$TAG loadStudentsWithParentsPage start school=$normalizedSchoolId q=${normalizedQuery.ifBlank { "<blank>" }} pageIndex=$safePageIndex pageSize=$safePageSize offset=$offset forceRemote=$forceRemote"
        )
        return KtorApiClient.getStudentsWithParents(
            schoolId = normalizedSchoolId,
            query = normalizedQuery.ifBlank { null },
            isActive = isActive,
            limit = safePageSize,
            offset = offset,
            sortBy = sort.normalizedField,
            order = sort.order.apiValue
        ).onSuccess { rows ->
            scLog("$TAG loadStudentsWithParentsPage complete school=$normalizedSchoolId pageIndex=$safePageIndex count=${rows.size}")
        }.onFailure { throwable ->
            scLog("$TAG loadStudentsWithParentsPage failure school=$normalizedSchoolId pageIndex=$safePageIndex", throwable)
        }
    }

    suspend fun loadSchoolParentsPage(
        schoolId: String,
        query: String = "",
        classSectionId: String? = null,
        isActive: Boolean? = null,
        advancedFilter: ParentFilterRequest? = null,
        pageIndex: Int = 0,
        pageSize: Int = PaginationPolicy.DEFAULT_PAGE_SIZE,
        sort: ServerSort = ServerSort("name", com.schoolconnect.app.feature.common.ServerSortOrder.ASC),
        forceRemote: Boolean = false
    ): Result<List<StudentListResponse>> {
        val normalizedSchoolId = schoolId.trim()
        if (normalizedSchoolId.isBlank()) {
            return Result.failure(IllegalArgumentException("schoolId is required"))
        }

        val normalizedQuery = query.trim()
        val normalizedClassSectionId = classSectionId?.trim()?.takeIf { it.isNotBlank() }
        val safePageIndex = pageIndex.coerceAtLeast(0)
        val safePageSize = pageSize.coerceAtLeast(1)
        val cacheKey = ParentsPageCacheKey(
            schoolId = normalizedSchoolId,
            query = normalizedQuery,
            classSectionId = normalizedClassSectionId,
            isActive = isActive,
            pageIndex = safePageIndex,
            pageSize = safePageSize,
            sort = sort.cacheKey
        )

        cachedParentsPage?.takeIf { !forceRemote && advancedFilter == null && it.key == cacheKey }?.let { cache ->
            scLog(
                "$TAG loadSchoolParentsPage cacheHit school=$normalizedSchoolId q=${normalizedQuery.ifBlank { "<blank>" }} isActive=${isActive?.toString() ?: "<any>"} pageIndex=$safePageIndex pageSize=$safePageSize count=${cache.parents.size}"
            )
            return returnCachedAndRefresh(scope, TAG, "loadSchoolParentsPage", "school=$normalizedSchoolId pageIndex=$safePageIndex pageSize=$safePageSize", cache.parents) {
                loadSchoolParentsPage(
                    schoolId = normalizedSchoolId,
                    query = query,
                    classSectionId = classSectionId,
                    isActive = isActive,
                    advancedFilter = null,
                    pageIndex = safePageIndex,
                    pageSize = safePageSize,
                    sort = sort,
                    forceRemote = true
                )
            }
        }

        val offset = safePageIndex * safePageSize
        scLog(
            "$TAG loadSchoolParentsPage start school=$normalizedSchoolId q=${normalizedQuery.ifBlank { "<blank>" }} isActive=${isActive?.toString() ?: "<any>"} pageIndex=$safePageIndex pageSize=$safePageSize offset=$offset forceRemote=$forceRemote"
        )
        return runCatching {
            val parents = if (advancedFilter != null) {
                val response = KtorApiClient.filterParentsWithStudents(
                    schoolId = normalizedSchoolId,
                    request = advancedFilter,
                    sortBy = sort.normalizedField,
                    order = sort.order.apiValue,
                    limit = safePageSize,
                    offset = offset
                ).getOrElse { throwable -> throw throwable }
                val rows = response.flatMap { it.toStudentRows() }
                rows.withServerTotalCount(response.serverTotalCountOrNull())
            } else KtorApiClient.getStudentsWithParents(
                schoolId = normalizedSchoolId,
                query = normalizedQuery.ifBlank { null },
                classSectionId = normalizedClassSectionId,
                isActive = isActive,
                limit = safePageSize,
                offset = offset,
                sortBy = sort.normalizedField,
                order = sort.order.apiValue
            ).getOrElse { throwable ->
                scLog(
                    "$TAG loadSchoolParentsPage fetchFailure school=$normalizedSchoolId pageIndex=$safePageIndex pageSize=$safePageSize",
                    throwable
                )
                throw throwable
            }
            val dedupedParents = parents.distinctBy { it.id }
                .withServerTotalCount(parents.serverTotalCountOrNull())

            cachedParentsPage = CachedParentsPage(cacheKey, dedupedParents)
            scLog(
                "$TAG loadSchoolParentsPage complete school=$normalizedSchoolId q=${normalizedQuery.ifBlank { "<blank>" }} pageIndex=$safePageIndex pageSize=$safePageSize count=${dedupedParents.size}"
            )
            dedupedParents
        }.onFailure { throwable ->
            scLog(
                "$TAG loadSchoolParentsPage failure school=$normalizedSchoolId q=${normalizedQuery.ifBlank { "<blank>" }} pageIndex=$safePageIndex pageSize=$safePageSize",
                throwable
            )
        }
    }

    private fun ParentListResponse.toStudentRows(): List<StudentListResponse> {
        val parentRows = listOf(
            StudentParentListResponse(
                parentId = id,
                name = name,
                relationshipType = students.orEmpty().firstOrNull { it.isPrimaryContact }?.relationshipType,
                isPrimaryContact = true,
                mobile = primaryContactPhone,
                email = primaryContactEmail,
                isActive = isActive
            )
        )
        return students.orEmpty().map { student ->
            StudentListResponse(
                schoolId = schoolId,
                admissionNumber = student.admissionNumber,
                name = student.studentName,
                className = student.className,
                sectionName = student.sectionName,
                id = student.studentId,
                isActive = isActive,
                parents = parentRows
            )
        }.ifEmpty {
            listOf(
                StudentListResponse(
                    schoolId = schoolId,
                    name = name,
                    id = id,
                    isActive = isActive,
                    parents = parentRows
                )
            )
        }
    }

    suspend fun loadSchoolParents(
        schoolId: String,
        forceRemote: Boolean = false
    ): Result<List<StudentListResponse>> {
        val normalizedSchoolId = schoolId.trim()
        if (normalizedSchoolId.isBlank()) {
            return Result.failure(IllegalArgumentException("schoolId is required"))
        }

        cachedSchoolParents?.takeIf { !forceRemote && it.schoolId == normalizedSchoolId }?.let { cache ->
            scLog(
                "$TAG loadSchoolParents cacheHit school=$normalizedSchoolId count=${cache.parents.size}"
            )
            return Result.success(cache.parents)
        }

        scLog("$TAG loadSchoolParents start school=$normalizedSchoolId forceRemote=$forceRemote")
        return runCatching {
            val parents = loadPagedItems(
                tag = TAG,
                operation = "loadSchoolParents",
                pageSize = 100,
                maxPages = 100,
                startContext = "school=$normalizedSchoolId"
            ) { offset ->
                val page = KtorApiClient.getStudentsWithParents(
                    schoolId = normalizedSchoolId,
                    limit = 100,
                    offset = offset
                ).getOrElse { throwable ->
                    scLog(
                        "$TAG loadSchoolParents pageFailure school=$normalizedSchoolId offset=$offset",
                        throwable
                    )
                    throw throwable
                }
                scLog("$TAG loadSchoolParents page school=$normalizedSchoolId offset=$offset count=${page.size}")
                page
            }.distinctBy { it.id }

            cachedSchoolParents = CachedParents(normalizedSchoolId, parents)
            scLog(
                "$TAG loadSchoolParents complete school=$normalizedSchoolId count=${parents.size} active=${parents.count { it.isActive == true }} inactive=${parents.count { it.isActive != true }}"
            )
            parents
        }.onFailure { throwable ->
            scLog("$TAG loadSchoolParents failure school=$normalizedSchoolId", throwable)
        }
    }

    fun invalidateCache(schoolId: String? = null) {
        val normalizedSchoolId = schoolId?.trim()?.takeIf { it.isNotBlank() }
        if (normalizedSchoolId == null) {
            cachedSchoolParents = null
            cachedParentsPage = null
        } else {
            if (cachedSchoolParents?.schoolId == normalizedSchoolId) {
                cachedSchoolParents = null
            }
            if (cachedParentsPage?.key?.schoolId == normalizedSchoolId) {
                cachedParentsPage = null
            }
        }
    }
}
